import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { validateUploadFile } from './uploadValidation';
import {
  uploadAsset,
  discardUploads,
  discardUploadsOnUnload,
  type DiscardOutcome,
  type UploadContext,
} from './uploadAssetService';
import { toUploadedImageAsset } from './toUploadedImageAsset';
import { registerUnsavedWork } from '../../../services/unsaved-work';
import type { Asset } from '../../../types/asset.types';
import type { ImageAssetType } from '@/components/ui/carousel/carousel.types';

export type UploadFileStatus = 'uploading' | 'done' | 'error';

export interface UploadFile {
  id: string;
  name: string;
  size: number;
  status: UploadFileStatus;
  progress: number;
  error?: string;
  previewUrl?: string;
  asset?: Asset;
}

/**
 * Photos a discard could not remove: refused by asset-service, or never reached
 * after retries. They are still in the photoset server-side. `leftovers` are
 * grid-ready versions of them (server image URLs, since the modal's preview
 * blobs are revoked when it closes), so the page can show them for deletion.
 */
export interface DiscardReport {
  rejected: DiscardOutcome['rejected'];
  failed: string[];
  leftovers: ImageAssetType[];
}

export interface UploadQueueOptions {
  /** Called whenever a discard leaves photos behind for good. */
  onDiscardIncomplete?: (report: DiscardReport) => void;
}

const EMPTY_OUTCOME: DiscardOutcome = { discarded: [], rejected: [], failed: [] };

export function useUploadQueue(ctx: UploadContext, options: UploadQueueOptions = {}) {
  const [files, setFiles] = useState<UploadFile[]>([]);
  const seq = useRef(0);

  // Always reflects the latest files list so the unmount effect can read it
  // without taking a stale closure.
  const filesRef = useRef(files);
  filesRef.current = files;

  const onDiscardIncomplete = useRef(options.onDiscardIncomplete);
  onDiscardIncomplete.current = options.onDiscardIncomplete;

  // Set once the modal is being left (cancel or unmount). A discard that fails after that has no
  // later path to retry it, so it is reported instead of being kept for one.
  const closed = useRef(false);

  // Tracks preview URLs that were handed off to the consumer via takeReadyImages().
  // Those blob URLs must NOT be revoked — they live on as grid-tile image caches.
  const handedOff = useRef(new Set<string>());

  // One AbortController per in-flight upload, so removing a file or closing
  // the modal actually cancels the request instead of letting it finish in the
  // background and silently create an asset server-side. Settled uploads are
  // dropped from the map, so unmount only aborts what is still in flight.
  const controllers = useRef(new Map<string, AbortController>());

  // The pending upload request per file, so a cancel can wait for one it must not abort.
  const pending = useRef(new Map<string, Promise<Asset>>());

  // Files whose whole body has been sent. The server may already have saved,
  // back-linked and published such a photo before its response arrives, so
  // aborting would only lose the assetUUID needed to discard it. These are
  // awaited and discarded instead. A file still sending is safe to abort: the
  // server cannot persist half a multipart body.
  const fullySent = useRef(new Set<string>());

  // Every upload the server has answered, by file id, recorded the moment the
  // response arrives. The discard paths read THIS, not rendered `files`: a
  // response that has landed but not re-rendered yet is otherwise invisible to
  // both the "done" list and the in-flight map.
  // Kept with the photoset the file was uploaded into, read when the upload started: a leftover
  // belongs to THAT set, whatever the modal's photoset is by the time it is reported.
  const landed = useRef(new Map<string, { asset: Asset; photosetId?: string }>());

  // Asset UUIDs already settled with the server — handed off to the consumer on
  // confirm (must never be discarded), already discarded, or refused for good.
  const reconciled = useRef(new Set<string>());
  // Bumped whenever `reconciled` changes. The set is a ref, so mutating it does
  // not re-render; the unsaved-work registration below reads it and must
  // follow it (DV-3421, review of 0a17c730, item 10).
  const [reconciledVersion, setReconciledVersion] = useState(0);
  const reconciledChanged = useCallback(() => setReconciledVersion((v) => v + 1), []);

  // Discards that failed on a path with no later chance of its own (remove()):
  // the next cancel or the unmount net retries them.
  const retry = useRef(new Set<string>());

  // Discarded by the pagehide keepalive, whose outcome is never seen.
  const discardedOnUnload = useRef(new Set<string>());

  const report = useCallback((rejected: DiscardOutcome['rejected'], failed: string[]) => {
    if (rejected.length === 0 && failed.length === 0) return;
    const left = new Set([...rejected.map((r) => r.assetUUID), ...failed]);
    // Server images (asset.caches), not the modal's preview blobs: those are revoked on close.
    // Server image, not the modal's preview blob (revoked on close). The upload response only
    // has caches.fullSize (sizes come later, DV-2092), so it is used for every size, as on
    // confirm; the grid tile reads 300w/830w.
    const leftovers = Array.from(landed.current.values())
      .filter(({ asset }) => left.has(asset.assetUUID))
      .map(({ asset, photosetId }) =>
        toUploadedImageAsset(asset, asset.caches?.fullSize ?? asset.url ?? '', photosetId),
      );
    onDiscardIncomplete.current?.({ rejected, failed, leftovers });
  }, []);

  // Discards the given server-side assets the user is walking away from.
  // Aborting is useless once an upload has settled: the asset row, the S3
  // original and the photoset back-link all already exist, so the only way to
  // honour "Cancel" is to discard them. `final`: no later path will retry a
  // failure, so it is reported now; otherwise it is kept for the next one.
  const discardAssetUUIDs = useCallback(
    async (assetUUIDs: string[], final: boolean): Promise<DiscardOutcome> => {
      const targets = Array.from(new Set([...assetUUIDs, ...(final ? retry.current : [])]))
        .filter((id) => !reconciled.current.has(id));
      if (targets.length === 0) return EMPTY_OUTCOME;
      // Mark before awaiting so a concurrent discard path can't send them twice.
      targets.forEach((id) => {
        reconciled.current.add(id);
        retry.current.delete(id);
      });
      reconciledChanged();
      const outcome = await discardUploads(targets);
      // Decided AFTER the await: the modal may have been left while this request ran (a remove()
      // whose discard fails after Cancel has nothing left to retry it).
      if (final || closed.current) {
        // A rejection, or a failure no path will retry: reported once, and kept marked so no
        // later path sends (and reports) it again.
        report(outcome.rejected, outcome.failed);
      } else {
        // Kept for the next cancel or the unmount net, which include `retry`.
        outcome.failed.forEach((id) => {
          reconciled.current.delete(id);
          retry.current.add(id);
        });
        if (outcome.failed.length > 0) reconciledChanged();
        report(outcome.rejected, []);
      }
      return outcome;
    },
    [report, reconciledChanged],
  );

  // The assetUUIDs of uploads the server has answered (optionally only these file ids).
  const landedAssetUUIDs = useCallback(
    (ids?: string[]) =>
      Array.from(landed.current.entries())
        .filter(([id]) => !ids || ids.includes(id))
        .map(([, { asset }]) => asset.assetUUID),
    [],
  );

  // Stops the given in-flight uploads: aborts what is still sending, and waits for
  // what has been fully sent, returning the assetUUIDs those produced.
  const stopInFlight = useCallback(async (ids: string[]) => {
    const waiting: Promise<Asset>[] = [];
    ids.forEach((id) => {
      const request = pending.current.get(id);
      if (fullySent.current.has(id) && request) {
        waiting.push(request);
      } else {
        controllers.current.get(id)?.abort();
        controllers.current.delete(id);
      }
    });
    const settled = await Promise.allSettled(waiting);
    return settled.flatMap((r) => (r.status === 'fulfilled' ? [r.value.assetUUID] : []));
  }, []);

  // On unmount, stop in-flight uploads, discard anything that landed but was
  // never confirmed, and revoke any preview URL that was never handed off to the
  // consumer (e.g. the user closed the modal without confirming).
  useEffect(() => {
    const inFlight = controllers.current;
    return () => {
      closed.current = true;
      // Safety net for closes that bypass the explicit cancel path — a route change
      // or a parent unmount. Fire-and-forget: unmount cannot await.
      const alreadyLanded = landedAssetUUIDs();
      void stopInFlight(Array.from(inFlight.keys())).then((late) =>
        discardAssetUUIDs([...alreadyLanded, ...late], true),
      );
      filesRef.current.forEach((f) => {
        if (f.previewUrl && !handedOff.current.has(f.previewUrl)) {
          URL.revokeObjectURL(f.previewUrl);
        }
      });
    };
  }, [discardAssetUUIDs, landedAssetUUIDs, stopInFlight]);

  // A tab close, reload or navigation off the app does not unmount anything, and
  // the browser cancels in-app requests with the page. A keepalive request
  // survives it. Uploads still in flight are lost with the page either way.
  useEffect(() => {
    const onPageHide = () => {
      const targets = landedAssetUUIDs().filter((id) => !reconciled.current.has(id));
      // Not sent (no usable token): leave them retryable for a bfcache restore.
      if (targets.length === 0 || !discardUploadsOnUnload(targets)) return;
      targets.forEach((id) => {
        reconciled.current.add(id);
        discardedOnUnload.current.add(id);
      });
      reconciledChanged();
      // A discarded photo must not be confirmable if the page is restored.
      setFiles((fs) =>
        fs.map((f) =>
          f.asset && targets.includes(f.asset.assetUUID)
            ? { ...f, status: 'error', error: 'Removed when you left the page' }
            : f,
        ),
      );
    };
    // Restored from the back/forward cache: the keepalive's outcome was never
    // seen, so send those again through the normal path, which reports anything
    // that stayed behind. Idempotent server-side.
    const onPageShow = (event: PageTransitionEvent) => {
      if (!event.persisted || discardedOnUnload.current.size === 0) return;
      const again = Array.from(discardedOnUnload.current);
      discardedOnUnload.current.clear();
      again.forEach((id) => reconciled.current.delete(id));
      reconciledChanged();
      // Final only if the modal has been left: while it is open, a failure stays retryable by
      // the next cancel or the unmount net, as on any other open-modal path.
      void discardAssetUUIDs(again, closed.current).then((outcome) => {
        // Still in the photoset: the rows must stop saying they were removed.
        const kept = new Set([...outcome.rejected.map((r) => r.assetUUID), ...outcome.failed]);
        if (kept.size === 0) return;
        setFiles((fs) =>
          fs.map((f) =>
            f.asset && kept.has(f.asset.assetUUID)
              ? { ...f, status: 'done', error: undefined }
              : f,
          ),
        );
      });
    };
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [discardAssetUUIDs, landedAssetUUIDs, reconciledChanged]);

  const patch = useCallback((id: string, next: Partial<UploadFile>) => {
    setFiles((fs) => fs.map((f) => (f.id === id ? { ...f, ...next } : f)));
  }, []);

  const addFiles = useCallback(
    (incoming: FileList | File[]) => {
      Array.from(incoming).forEach((file) => {
        const id = `u${seq.current++}`;
        const check = validateUploadFile(file);
        if (!check.ok) {
          setFiles((fs) => [
            ...fs,
            { id, name: file.name, size: file.size, status: 'error', progress: 0, error: check.error },
          ]);
          return;
        }
        const previewUrl = URL.createObjectURL(file);
        setFiles((fs) => [
          ...fs,
          { id, name: file.name, size: file.size, status: 'uploading', progress: 0, previewUrl },
        ]);
        const controller = new AbortController();
        controllers.current.set(id, controller);
        const photosetId = ctx.photosetId;
        const request = uploadAsset(
          file,
          ctx,
          (pct) => {
            if (pct >= 100) fullySent.current.add(id);
            patch(id, { progress: pct });
          },
          controller.signal,
        );
        pending.current.set(id, request);
        request
          .then((asset) => {
            // Synchronously, before .finally drops the request from `pending`.
            landed.current.set(id, { asset, photosetId });
            patch(id, { status: 'done', progress: 100, asset });
          })
          .catch(() => {
            // An aborted upload was cancelled on purpose (file removed / modal
            // closed) — its row is already gone, don't resurrect it as an error.
            if (!controller.signal.aborted) patch(id, { status: 'error', error: 'Upload failed' });
          })
          .finally(() => {
            controllers.current.delete(id);
            pending.current.delete(id);
          });
      });
    },
    [ctx, patch],
  );

  const remove = useCallback(
    (id: string) => {
      // Read from the ref rather than inside the setFiles updater: the updater must stay
      // pure (React can invoke it twice in StrictMode, which would double-fire the discard).
      const target = filesRef.current.find((f) => f.id === id);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      // Aborting does nothing for an upload that already finished — discard it server-side.
      const alreadyLanded = landedAssetUUIDs([id]);
      void stopInFlight([id]).then((late) =>
        discardAssetUUIDs([...alreadyLanded, ...late], false),
      );
      setFiles((fs) => fs.filter((f) => f.id !== id));
    },
    [discardAssetUUIDs, landedAssetUUIDs, stopInFlight],
  );

  // Explicit cancel: stop everything in flight and discard everything that landed,
  // so closing the modal leaves no trace of the upload server-side. Anything left
  // behind is reported through onDiscardIncomplete.
  const discardAll = useCallback(async (): Promise<DiscardOutcome> => {
    closed.current = true;
    const alreadyLanded = landedAssetUUIDs();
    const late = await stopInFlight(Array.from(controllers.current.keys()));
    return discardAssetUUIDs([...alreadyLanded, ...late], true);
  }, [discardAssetUUIDs, landedAssetUUIDs, stopInFlight]);

  const uploading = files.some((f) => f.status === 'uploading');

  // DV-3421 review round 6, item E. An upload not yet confirmed is unsaved
  // work: a reload's pagehide discards it. Registered so a reload the app
  // starts by itself (cross-tab scope reconciliation) waits for it.
  // `reconciledVersion` re-runs this when the ref set changes (item 10).
  const unconfirmed = useMemo(
    () =>
      files.some(
        (f) =>
          f.status === 'uploading' ||
          (f.status === 'done' &&
            f.asset !== undefined &&
            !reconciled.current.has(f.asset.assetUUID)),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reconciledVersion stands in for the ref
    [files, reconciledVersion],
  );
  useEffect(() => (unconfirmed ? registerUnsavedWork() : undefined), [unconfirmed]);

  // Returns the ready images AND records their preview URLs as handed-off so
  // the unmount cleanup knows not to revoke them (they live on as grid caches).
  const takeReadyImages = useCallback((): ImageAssetType[] => {
    const images = filesRef.current
      .filter((f) => f.status === 'done' && f.asset && f.previewUrl)
      .map((f) => {
        handedOff.current.add(f.previewUrl as string);
        // Confirmed by the user — the discard paths must never delete these.
        reconciled.current.add((f.asset as Asset).assetUUID);
        return toUploadedImageAsset(f.asset as Asset, f.previewUrl as string, ctx.photosetId);
      });
    if (images.length > 0) reconciledChanged();
    return images;
  }, [ctx.photosetId, reconciledChanged]);

  return { files, addFiles, remove, uploading, takeReadyImages, discardAll };
}
